/**
 * PublicShopMap — Google Maps view of all public shops (no auth).
 * Route: /map
 *
 * Reuses the same Google Maps script-injection pattern as ShopsMapPage.
 * Persists map center/zoom to sessionStorage so returning preserves viewport.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useShopStore } from '../../stores/shopStore'
import './PublicShopMap.css'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
const MAP_STATE_KEY = 'pubmap_viewport'

interface SavedViewport {
  lat: number
  lng: number
  zoom: number
}

function saveViewport(v: SavedViewport) {
  try { sessionStorage.setItem(MAP_STATE_KEY, JSON.stringify(v)) } catch {}
}

function loadViewport(): SavedViewport | null {
  try {
    const raw = sessionStorage.getItem(MAP_STATE_KEY)
    if (!raw) return null
    const v = JSON.parse(raw)
    if (typeof v.lat === 'number' && typeof v.lng === 'number' && typeof v.zoom === 'number') return v
  } catch {}
  return null
}

function loadGoogleMapsScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
      resolve()
      return
    }
    const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Google Maps script failed')))
      return
    }
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly`
    script.async = true
    script.defer = true
    script.onload = () => {
      if (typeof google !== 'undefined' && google.maps) resolve()
      else reject(new Error('google.maps not available'))
    }
    script.onerror = () => reject(new Error('Failed to load Google Maps'))
    document.head.appendChild(script)
  })
}

export default function PublicShopMap() {
  const navigate = useNavigate()
  const { t } = useTranslation('shops')
  const { publicShops, publicShopsLoading, fetchPublicShops } = useShopStore()
  const mapRef = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const markersRef = useRef<google.maps.Marker[]>([])
  const initRef = useRef(false)
  /** True after first marker layout — prevents fitBounds from overwriting a restored viewport */
  const didFitRef = useRef(false)

  const shopsWithLocation = publicShops.filter((s) => s.location !== null)

  useEffect(() => { fetchPublicShops() }, [])

  // Init map — restore saved viewport if available
  useEffect(() => {
    if (!mapRef.current || initRef.current) return
    if (!GOOGLE_MAPS_API_KEY) {
      setError(t('errors.mapsApiKeyNotConfigured'))
      setLoading(false)
      return
    }
    let cancelled = false
    initRef.current = true
    ;(window as any).gm_authFailure = () => {
      if (!cancelled) {
        setError(t('errors.mapsApiKeyRestricted'))
        setLoading(false)
      }
    }

    const init = async () => {
      try {
        await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY)
        if (cancelled || !mapRef.current) return

        const saved = loadViewport()
        const mapInstance = new google.maps.Map(mapRef.current, {
          center: saved ? { lat: saved.lat, lng: saved.lng } : { lat: 25.0762, lng: 55.1329 },
          zoom: saved ? saved.zoom : 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        })

        // If we restored a saved viewport, skip the first fitBounds in the markers effect
        if (saved) didFitRef.current = true

        // Persist viewport on every pan/zoom
        mapInstance.addListener('idle', () => {
          const c = mapInstance.getCenter()
          const z = mapInstance.getZoom()
          if (c && z !== undefined) saveViewport({ lat: c.lat(), lng: c.lng(), zoom: z })
        })

        setMap(mapInstance)
        setLoading(false)
      } catch {
        if (!cancelled) {
          setError(t('errors.failedToLoadMaps'))
          setLoading(false)
        }
      }
    }
    init()
    return () => {
      cancelled = true
      initRef.current = false
      delete (window as any).gm_authFailure
    }
  }, [publicShops])

  // Markers
  useEffect(() => {
    if (!map) return
    markersRef.current.forEach((m) => m.setMap(null))
    markersRef.current = []
    if (shopsWithLocation.length === 0) return

    shopsWithLocation.forEach((shop) => {
      const marker = new google.maps.Marker({
        position: { lat: shop.location!.lat, lng: shop.location!.lng },
        map,
        title: shop.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#3b82f6',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
      })
      markersRef.current.push(marker)

      const content = `
        <div style="padding:8px;min-width:180px;">
          <h3 style="margin:0 0 6px;font-size:15px;font-weight:600;color:#111;">${shop.name}</h3>
          ${shop.category ? `<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;background:#dbeafe;color:#1e40af;margin-bottom:6px;">${shop.category}</span>` : ''}
          ${shop.address ? `<p style="margin:4px 0;font-size:12px;color:#666;">${shop.address}</p>` : ''}
          <div style="margin-top:10px;display:flex;gap:6px;">
            <button onclick="window.__pubMapOpenShop('${shop.id}')" style="padding:6px 12px;background:#3b82f6;color:white;border:none;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;">${t('viewShop')}</button>
          </div>
        </div>
      `
      const infoWindow = new google.maps.InfoWindow({ content })
      marker.addListener('click', () => infoWindow.open(map, marker))
    })

    // Only auto-fit bounds on the first load when we didn't restore a saved viewport
    if (!didFitRef.current) {
      if (shopsWithLocation.length > 1) {
        const bounds = new google.maps.LatLngBounds()
        shopsWithLocation.forEach((s) => bounds.extend(s.location!))
        map.fitBounds(bounds, 50)
      } else {
        map.setCenter(shopsWithLocation[0].location!)
        map.setZoom(15)
      }
    }
    didFitRef.current = true
  }, [map, publicShops])

  // InfoWindow callback — navigate to 3D viewer via full page load
  // (shopIdFromUrl in main.tsx is parsed once at module load, so React Router navigate won't work)
  useEffect(() => {
    ;(window as any).__pubMapOpenShop = (shopId: string) => {
      window.location.href = `/?shopid=${shopId}`
    }
    return () => { delete (window as any).__pubMapOpenShop }
  }, [])

  const handleCenterOnMe = () => {
    if (!map || !navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => { map.setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }); map.setZoom(15) },
      (err) => alert(`Could not get location: ${err.message}`),
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  return (
    <div className="pub-map-page">
      <header className="pub-map-header">
        <button className="pub-map-back" onClick={() => navigate('/')}>← {t('common:back')}</button>
        <h1>{t('shopMap')}</h1>
        <button className="pub-map-list-btn" onClick={() => navigate('/browse')}>📋</button>
      </header>

      {loading && (
        <div className="pub-map-loading">
          <div className="pub-map-spinner" />
          <p>{publicShopsLoading ? t('loadingShops') : t('loadingMap')}</p>
        </div>
      )}

      {error && (
        <div className="pub-map-error">
          <p>⚠️ {error}</p>
          <button onClick={() => navigate('/')}>{t('common:backToHome')}</button>
        </div>
      )}

      {!error && <div ref={mapRef} className="pub-map-container" />}

      {map && !error && (
        <>
          <button className="pub-map-center-btn" onClick={handleCenterOnMe}>{t('nearMe')}</button>
          <div className="pub-map-info">
            {!publicShopsLoading && shopsWithLocation.length === 0
              ? t('noPublicShopsOnMap')
              : t('shopsOnMap', { count: shopsWithLocation.length })}
          </div>
        </>
      )}
    </div>
  )
}
